import { useFormikContext } from 'formik';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { fetchCities, fetchZipcodes } from 'containers/App/actions';
import useCollectJS from 'hooks/useCollectJS';
import AddressInput from 'v2/forms/AddressInput';
import Form from 'v2/forms/Form';
import Input from 'v2/forms/Input';
import Label from 'v2/forms/Label';
import PhoneInput from 'v2/forms/PhoneInput';
import Select from 'v2/forms/Select';

import { paymentOptions } from './constants';
import { ACHForm, CreditCardForm } from './forms';
import { getFields } from './utils';

// Ad-blockers routinely block secure.safewebservices.com, and the load
// failure this produces used to be console-only — a dead disabled Next
// button with nothing telling the patient (or support) why. Exported so
// tests can assert the exact copy without duplicating it.
export const PAYMENT_FORM_LOAD_ERROR_MESSAGE =
	'The payment form failed to load. Please disable any ad-blocker extensions, then refresh the page or try again.';

export default function PaymentForm(props) {
	const { onSaveToken, defaultAddress, onSecretSkip, onReadyChange } = props;
	const dispatch = useDispatch();

	const [sameAsMainAddress, setSameAsMainAddress] = useState(false);

	const formik = useFormikContext();
	const { values, setFieldValue, setFieldTouched } = formik;
	const { isCCForm } = values;

	const [paymentToken, setPaymentToken] = useState('');
	const [address, setAddress] = useState('');

	const [errors, setErrors] = useState();

	const onValidate = useCallback(
		(name, isValid, reason) => {
			setErrors((oldErrors) => ({
				...oldErrors,
				[name]: { isValid, reason },
			}));
		},
		[errors],
	);

	const fields = getFields(isCCForm);

	const { isReady, error, retry } = useCollectJS(
		({ token }) => setPaymentToken(token),
		onValidate,
		fields,
		[isCCForm],
	);

	// Formik validates the fields it owns, but the card/bank inputs live in
	// cross-origin Collect.js iframes it cannot see. Parents gate their submit
	// button on this so Next is not enabled before those fields exist.
	useEffect(() => {
		onReadyChange?.(isReady);
	}, [isReady, onReadyChange]);

	const handleSave = useCallback(() => {
		onSaveToken({
			...values,
			paymentToken,
		});
	}, [paymentToken, values]);

	useEffect(() => {
		if (!paymentToken) return;

		handleSave();
	}, [paymentToken]);

	function toggleAddress() {
		let value;
		if (sameAsMainAddress) {
			value = {
				street: '',
				city: '',
				state: '',
				zipcode: '',
			};
			setSameAsMainAddress(false);
		} else {
			setSameAsMainAddress(true);
			dispatch(fetchCities(defaultAddress.state));
			dispatch(fetchZipcodes(defaultAddress.city));
			value = {
				street: defaultAddress.street,
				city: defaultAddress.city,
				state: defaultAddress.state,
				zipcode: defaultAddress.zipcode,
			};
		}

		setFieldTouched(true);
		setFieldValue('address', value);
	}

	function handleChange(event) {
		const {
			target: { name, value },
		} = event;

		if (name === 'street') {
			setSameAsMainAddress(false);
			setAddress({
				...address,
				street: value,
			});

			return;
		}
	}

	if (error) {
		return (
			<Form>
				<div className="card d-flex flex-column p-4 bb-0">
					<div className="alert alert-danger" role="alert">
						{PAYMENT_FORM_LOAD_ERROR_MESSAGE}
					</div>
					<button
						type="button"
						className="btn btn-primary btn-lg btn-color text-uppercase font-15 align-self-start"
						onClick={retry}
					>
						Retry
					</button>
				</div>
				<hr className="m-0" />
			</Form>
		);
	}

	return (
		<Form>
			<div className="card d-flex flex-column p-4 bb-0">
				<div className="row">
					<div className="col-auto pr-0">
						<Label className="font-19 mt-2">Payment Method</Label>
					</div>
					<div className="col-auto" style={{ width: 240 }}>
						<Select
							options={paymentOptions}
							id="isCCForm"
							name="isCCForm"
							onChange={({ value }) => {
								if (value !== isCCForm) {
									setErrors({});
								}
							}}
						/>
					</div>
				</div>
				{isCCForm ? (
					<CreditCardForm errors={errors} />
				) : (
					<ACHForm errors={errors} />
				)}
				<div className="form-row">
					<div className="col-md-6">
						<PhoneInput
							label="payer phone number"
							id="userInfo.phone"
							name="userInfo.phone"
							onChange={(value) =>
								handleChange({
									target: { name: 'phone', value },
								})
							}
							placeholder="###-###-####"
							required
						/>
					</div>
					<div className="col-md-6">
						<Input
							label="payer email"
							id="userInfo.email"
							name="userInfo.email"
							placeholder="johndoe@gmail.com"
							required
							onChange={(e) => {
								formik.handleChange(e);
								// onSecretSkip is optional: adult-OP intake passes it to detect
								// the SKIP-PAYMENT-2025 phrase, but IOP omits it on purpose (a
								// guarantor checkbox already bypasses payment there). Guard the
								// call so an IOP keystroke doesn't throw. See MTC-088.
								if (e.target.value.toUpperCase() === 'SKIP-PAYMENT-2025') {
									onSecretSkip?.(true);
								} else {
									onSecretSkip?.(false);
								}
							}}
						/>
					</div>
				</div>
				<div className="mt-4">
					<h4>Billing Address </h4>
					<div className="font-12 mb-1">
						Same as main address{' '}
						<input
							type="checkbox"
							checked={sameAsMainAddress}
							onChange={toggleAddress}
							className="ml-1"
						/>
					</div>
					<div className="mb-3">
						<AddressInput
							id="address"
							name="address"
							handleChange={() => setSameAsMainAddress(false)}
						/>
					</div>
				</div>
			</div>
			<hr className="m-0" />
		</Form>
	);
}
